/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as React from 'react';
import ModalWindow from 'apmui/components/ModalWindow';
import RichText from 'apmui/components/markup/RichTextView';
import Input from 'apmui/components/form/Input';
import bind from 'apmui/decorators/bind';
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';

import { IDialogAction, IDialogText, CommonDialogAction, IDialogConfiguration } from './Model';

const DIALOG_FOCUS_SELECTOR =
    ".apmui-button:not([tabindex='-1']), " +
    ".form-input-container input:not([disabled]):not([tabindex='-1']), " +
    ".form-input-container select:not([disabled]):not([tabindex='-1'])";

const app = App.get();

class ClientAction extends React.Component<IDialogAction & { dialog: IDialogConfiguration }> {
    @bind
    onClick() {
        app.dispatch(EventTags.DIALOG_ACTION, {
            dialog: this.props.dialog,
            action: this.props.id,
        });
    }

    render() {
        if (this.props.hidden) {
            return null;
        }

        let buttonStyle = 'apmui-button';
        if (this.props.style) {
            buttonStyle += ' apmui-button-' + this.props.style;
        }

        return (
            <div className={'apmui-dialog-action ' + this.props.id}>
                <button onClick={this.onClick} className={buttonStyle}>
                    {this.props.caption}
                </button>
                {this.props.description && (
                    <span className="apmui-dialog-action--description">
                        <RichText text={this.props.description} />
                    </span>
                )}
            </div>
        );
    }
}

class DialogView extends React.Component<IDialogConfiguration> {
    @bind
    onFieldChange(name: string, value: string | boolean) {
        app.dispatch(EventTags.DIALOG_ACTION, {
            action: CommonDialogAction.UPDATE,
            dialog: this.props,
            field: name,
            value,
        });
    }

    @bind
    onClose() {
        app.dispatch(EventTags.DIALOG_ACTION, {
            action: CommonDialogAction.CANCEL,
            dialog: this.props,
        });
    }

    textEntry(entry: IDialogText) {
        if (entry.text === '') {
            return null;
        }
        return (
            <div className={'apmui-dialog-text ' + entry.id}>
                <RichText text={entry.text} />
            </div>
        );
    }

    entries() {
        if (this.props.entries) {
            return this.props.entries.map((entry) => {
                if ('type' in entry) {
                    return <Input key={entry.name} onChange={this.onFieldChange} {...entry} />;
                } else if ('text' in entry) {
                    return this.textEntry(entry);
                } else {
                    return <ClientAction key={entry.id} dialog={this.props} {...entry} />;
                }
            });
        }
        return null;
    }

    render() {
        const entries = this.entries();
        const onClose = this.props.showCloseButton ? this.onClose : undefined;
        return (
            <ModalWindow
                id={this.props.id}
                title={this.props.title}
                onClose={onClose}
                closeTitle={this.props.closeTitle}
                focusSelector={DIALOG_FOCUS_SELECTOR}>
                <div className="apmui-form">
                    {this.props.resource && (
                        <div className="apmui-webtop-resource--caption-container">
                            <span className="apmui-webtop-resource--icon">
                                <img src={this.props.resource.icon} alt="" />
                            </span>
                            <span className="apmui-webtop-resource--caption">{this.props.resource.caption}</span>
                        </div>
                    )}
                    {this.props.body && (
                        <div>
                            <RichText text={this.props.body} />
                        </div>
                    )}
                    {entries}
                </div>
            </ModalWindow>
        );
    }
}

export default DialogView;
